import { create } from 'zustand'

export interface PendingMessage {
  id: string
  role: string
  parts: Record<string, unknown>[]
}

/** Module-level timers for auto-clearing stale typing state (90s) */
const typingTimers = new Map<string, ReturnType<typeof setTimeout>>()
const STALE_TIMEOUT = 90 * 1000

interface ChatStatusStore {
  /** creativeId → active tool names (empty array = thinking, no specific tool) */
  typingCreatives: Map<string, string[]>
  unreadCreatives: Set<string>
  pendingMessages: Map<string, PendingMessage[]>

  setTyping: (creativeId: string, typing: boolean, tools?: string[]) => void
  markUnread: (creativeId: string) => void
  clearUnread: (creativeId: string) => void
  storePendingMessage: (creativeId: string, message: PendingMessage) => void
  /** Drain the FIFO of messages received for this creative (oldest first). */
  consumePendingMessages: (creativeId: string) => PendingMessage[]
  /** Remove all state for a creative (call on delete) */
  clearCreative: (creativeId: string) => void
}

export const useChatStatusStore = create<ChatStatusStore>((set, get) => ({
  typingCreatives: new Map(),
  unreadCreatives: new Set(),
  pendingMessages: new Map(),

  setTyping: (creativeId, typing, tools) => {
    // Clear any existing stale timer
    const existing = typingTimers.get(creativeId)
    if (existing) clearTimeout(existing)

    if (typing) {
      // Set a stale auto-clear timer
      typingTimers.set(
        creativeId,
        setTimeout(() => {
          typingTimers.delete(creativeId)
          set(state => {
            const next = new Map(state.typingCreatives)
            next.delete(creativeId)
            return { typingCreatives: next }
          })
        }, STALE_TIMEOUT)
      )
      set(state => {
        const next = new Map(state.typingCreatives)
        next.set(creativeId, tools ?? [])
        return { typingCreatives: next }
      })
    } else {
      typingTimers.delete(creativeId)
      set(state => {
        if (!state.typingCreatives.has(creativeId)) return state
        const next = new Map(state.typingCreatives)
        next.delete(creativeId)
        return { typingCreatives: next }
      })
    }
  },

  markUnread: creativeId => {
    set(state => {
      if (state.unreadCreatives.has(creativeId)) return state
      const next = new Set(state.unreadCreatives)
      next.add(creativeId)
      return { unreadCreatives: next }
    })
  },

  clearUnread: creativeId => {
    set(state => {
      if (!state.unreadCreatives.has(creativeId)) return state
      const next = new Set(state.unreadCreatives)
      next.delete(creativeId)
      return { unreadCreatives: next }
    })
  },

  storePendingMessage: (creativeId, message) => {
    set(state => {
      // FIFO, not a single slot: with two active tabs, a second completed
      // message used to overwrite the first before the open chat (busy with
      // its own turn) got a chance to inject it — the other tab's side of the
      // conversation silently vanished. Dedupe by id so Pusher re-delivery and
      // the truncation-refetch path stay idempotent.
      const queue = state.pendingMessages.get(creativeId) ?? []
      if (queue.some(m => m.id === message.id)) return state
      const next = new Map(state.pendingMessages)
      next.set(creativeId, [...queue, message])
      return { pendingMessages: next }
    })
  },

  consumePendingMessages: creativeId => {
    const queue = get().pendingMessages.get(creativeId)
    if (queue?.length) {
      set(state => {
        const next = new Map(state.pendingMessages)
        next.delete(creativeId)
        return { pendingMessages: next }
      })
    }
    return queue ?? []
  },

  clearCreative: creativeId => {
    const timer = typingTimers.get(creativeId)
    if (timer) {
      clearTimeout(timer)
      typingTimers.delete(creativeId)
    }
    set(state => {
      const hasTyping = state.typingCreatives.has(creativeId)
      const hasUnread = state.unreadCreatives.has(creativeId)
      const hasPending = state.pendingMessages.has(creativeId)
      if (!hasTyping && !hasUnread && !hasPending) return state

      const typing = hasTyping
        ? (() => {
            const m = new Map(state.typingCreatives)
            m.delete(creativeId)
            return m
          })()
        : state.typingCreatives
      const unread = hasUnread
        ? (() => {
            const s = new Set(state.unreadCreatives)
            s.delete(creativeId)
            return s
          })()
        : state.unreadCreatives
      const pending = hasPending
        ? (() => {
            const m = new Map(state.pendingMessages)
            m.delete(creativeId)
            return m
          })()
        : state.pendingMessages
      return { typingCreatives: typing, unreadCreatives: unread, pendingMessages: pending }
    })
  },
}))
